import React, { useState, useEffect } from 'react';
import {
    Key, Plus, Edit2, Trash2, Save, X,
    Calendar, Clock, CheckCircle, XCircle,
    AlertCircle, PictureInPictureIcon, Bell,
    Lock,
} from 'lucide-react';

import API_BASE_URL from "../config";

export default function Settingss({ setCurrentPage }) {
    const [licenses, setLicenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [formData, setFormData] = useState({
        licenseKeyValue: '',
        expiredDate: '',
        isActive: true
    });

    // ✅ Get current user + check if SYSTEM ADMIN
    const [currentUser, setCurrentUser] = useState(null);
    useEffect(() => {
        const userData = localStorage.getItem("user");
        if (userData) {
            try { setCurrentUser(JSON.parse(userData)); } catch { }
        }
    }, []);

    const isSystemAdmin =
        currentUser?.positionName === 'SYSTEM ADMIN' ||
        currentUser?.position === 'SYSTEM ADMIN' ||
        currentUser?.role === 'SYSTEM ADMIN' ||
        currentUser?.positionId === 'SYSTEM ADMIN';

    useEffect(() => {
        fetchLicenses();
    }, []);

    const fetchLicenses = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}api/LicenseKey`);
            const data = await response.json();
            setLicenses(data);
        } catch (error) {
            console.error('Failed to fetch licenses:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (license) => {
        if (!isSystemAdmin) return;
        setEditingId(license.id);
        setFormData({
            licenseKeyValue: license.licenseKeyValue,
            expiredDate: license.expiredDate.split('T')[0],
            isActive: license.isActive
        });
    };

    const handleSave = async (id) => {
        if (!isSystemAdmin) return;
        try {
            const response = await fetch(`${API_BASE_URL}api/LicenseKey/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id,
                    ...formData,
                    expiredDate: new Date(formData.expiredDate).toISOString()
                })
            });
            if (response.ok) {
                await fetchLicenses();
                setEditingId(null);
                resetForm();
            }
        } catch (error) {
            console.error('Failed to update license:', error);
        }
    };

    const handleAdd = async () => {
        if (!isSystemAdmin) return;
        try {
            const response = await fetch(`${API_BASE_URL}api/LicenseKey`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    createDate: new Date().toISOString(),
                    expiredDate: new Date(formData.expiredDate).toISOString()
                })
            });
            if (response.ok) {
                await fetchLicenses();
                setShowAddForm(false);
                resetForm();
            }
        } catch (error) {
            console.error('Failed to add license:', error);
        }
    };

    const handleDelete = async (id) => {
        if (!isSystemAdmin) return;
        try {
            const response = await fetch(`${API_BASE_URL}api/LicenseKey/${id}`, {
                method: 'DELETE'
            });
            if (response.ok) {
                await fetchLicenses();
            }
        } catch (error) {
            console.error('Failed to delete license:', error);
        }
    };

    const resetForm = () => {
        setFormData({
            licenseKeyValue: '',
            expiredDate: '',
            isActive: true
        });
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const isExpired = (dateString) => {
        return new Date(dateString) < new Date();
    };

    const cards = [
        {
            title: "Change Password",
            page: "ChangePassword",
            icon: Lock,
            color: "from-indigo-500 to-indigo-600",
            bgColor: "bg-indigo-50",
            textColor: "text-indigo-600",
        },
        {
            title: "My Profile",
            page: "MyProfile",
            icon: PictureInPictureIcon,
            color: "from-blue-500 to-blue-600",
            bgColor: "bg-blue-50",
            textColor: "text-blue-600",
        },
        {
            title: "Notification Settings",
            page: "NotificationSettings",
            icon: Bell,
            color: "from-amber-500 to-orange-500",
            bgColor: "bg-amber-50",
            textColor: "text-amber-600",
        },
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen from-slate-50 via-blue-50 to-indigo-100 p-8">
            <div className="max-w-6xl mx-auto space-y-8">

                {/* Header */}
                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-2xl shadow-lg">
                                <Key className="text-white" size={32} />
                            </div>
                            <div>
                                <h1 className="text-4xl font-black bg-gradient-to-r from-indigo-600 via-blue-600 to-pink-600 bg-clip-text text-transparent">
                                    Settings
                                </h1>
                                <p className="text-gray-500 text-sm mt-1">Manage your application license keys</p>
                            </div>
                        </div>
                        {/* ✅ Add New License — SYSTEM ADMIN lang */}
                        {isSystemAdmin && (
                            <button
                                onClick={() => setShowAddForm(true)}
                                className="flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg transition-all hover:scale-105"
                            >
                                <Plus size={20} />
                                Add New License
                            </button>
                        )}
                    </div>
                </div>

                {/* Add Form — SYSTEM ADMIN lang */}
                {isSystemAdmin && showAddForm && (
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
                                <Plus className="text-white" size={24} />
                            </div>
                            <h2 className="text-2xl font-black text-gray-800">Add New License Key</h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">License Key</label>
                                <input
                                    type="text"
                                    value={formData.licenseKeyValue}
                                    onChange={(e) => setFormData({ ...formData, licenseKeyValue: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                                    placeholder="Enter license key"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Expiration Date</label>
                                <input
                                    type="date"
                                    value={formData.expiredDate}
                                    onChange={(e) => setFormData({ ...formData, expiredDate: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-3 mb-6">
                            <input
                                type="checkbox"
                                checked={formData.isActive}
                                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                className="w-5 h-5 text-indigo-600 rounded"
                            />
                            <label className="text-sm font-bold text-gray-700">Active License</label>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={handleAdd}
                                className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg transition-all hover:scale-105"
                            >
                                <Save size={20} />
                                Save License
                            </button>
                            <button
                                onClick={() => { setShowAddForm(false); resetForm(); }}
                                className="flex items-center gap-2 bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-bold hover:bg-gray-300 transition-all"
                            >
                                <X size={20} />
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {/* License Cards */}
                <div className="grid grid-cols-1 gap-6">
                    {licenses.map((license) => {
                        const expired = isExpired(license.expiredDate);
                        const isEditing = editingId === license.id;

                        return (
                            <div
                                key={license.id}
                                className={`bg-white rounded-3xl p-8 shadow-lg border-2 transition-all hover:shadow-xl ${expired ? 'border-red-200 bg-red-50/30' :
                                    license.isActive ? 'border-green-200 bg-green-50/30' : 'border-gray-200'
                                    }`}
                            >
                                {/* ✅ Edit mode — SYSTEM ADMIN lang makakapasok dito */}
                                {isEditing && isSystemAdmin ? (
                                    <div className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-2">License Key</label>
                                                <input
                                                    type="text"
                                                    value={formData.licenseKeyValue}
                                                    onChange={(e) => setFormData({ ...formData, licenseKeyValue: e.target.value })}
                                                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-2">Expiration Date</label>
                                                <input
                                                    type="date"
                                                    value={formData.expiredDate}
                                                    onChange={(e) => setFormData({ ...formData, expiredDate: e.target.value })}
                                                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                                                />
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <input
                                                type="checkbox"
                                                checked={formData.isActive}
                                                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                                className="w-5 h-5 text-indigo-600 rounded"
                                            />
                                            <label className="text-sm font-bold text-gray-700">Active License</label>
                                        </div>

                                        <div className="flex gap-3">
                                            <button
                                                onClick={() => handleSave(license.id)}
                                                className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg transition-all hover:scale-105"
                                            >
                                                <Save size={20} />
                                                Save Changes
                                            </button>
                                            <button
                                                onClick={() => { setEditingId(null); resetForm(); }}
                                                className="flex items-center gap-2 bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-bold hover:bg-gray-300 transition-all"
                                            >
                                                <X size={20} />
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    // View Mode
                                    <div>
                                        <div className="flex items-start justify-between mb-6">
                                            <div className="flex items-center gap-4">
                                                <div className={`p-3 rounded-2xl ${expired ? 'bg-gradient-to-br from-red-500 to-red-600' :
                                                    license.isActive ? 'bg-gradient-to-br from-green-500 to-green-600' :
                                                        'bg-gradient-to-br from-gray-400 to-gray-500'
                                                    }`}>
                                                    <Key className="text-white" size={28} />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h3 className="text-2xl font-black text-gray-800">{license.licenseKeyValue}</h3>
                                                        {expired ? (
                                                            <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-full">EXPIRED</span>
                                                        ) : license.isActive ? (
                                                            <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">ACTIVE</span>
                                                        ) : (
                                                            <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-bold rounded-full">INACTIVE</span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-gray-500">License ID: #{license.id}</p>
                                                </div>
                                            </div>

                                            {/* ✅ Edit + Delete buttons — SYSTEM ADMIN lang makakakita */}
                                            {isSystemAdmin && (
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleEdit(license)}
                                                        className="p-3 bg-blue-100 text-blue-600 rounded-xl hover:bg-blue-200 transition-all hover:scale-110"
                                                        title="Edit License"
                                                    >
                                                        <Edit2 size={20} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(license.id)}
                                                        className="p-3 bg-red-100 text-red-600 rounded-xl hover:bg-red-200 transition-all hover:scale-110"
                                                        title="Delete License"
                                                    >
                                                        <Trash2 size={20} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                            <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 border border-blue-200">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Clock className="text-blue-600" size={16} />
                                                    <span className="text-xs font-bold text-blue-700">Created Date</span>
                                                </div>
                                                <p className="text-lg font-bold text-gray-800">{formatDate(license.createDate)}</p>
                                            </div>

                                            <div className={`rounded-xl p-4 border ${expired ? 'bg-gradient-to-br from-red-50 to-white border-red-200' :
                                                'bg-gradient-to-br from-green-50 to-white border-green-200'
                                                }`}>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Calendar className={expired ? 'text-red-600' : 'text-green-600'} size={16} />
                                                    <span className={`text-xs font-bold ${expired ? 'text-red-700' : 'text-green-700'}`}>
                                                        Expiration Date
                                                    </span>
                                                </div>
                                                <p className="text-lg font-bold text-gray-800">{formatDate(license.expiredDate)}</p>
                                            </div>

                                            <div className={`rounded-xl p-4 border ${license.isActive ? 'bg-gradient-to-br from-green-50 to-white border-green-200' :
                                                'bg-gradient-to-br from-gray-50 to-white border-gray-200'
                                                }`}>
                                                <div className="flex items-center gap-2 mb-2">
                                                    {license.isActive ? (
                                                        <CheckCircle className="text-green-600" size={16} />
                                                    ) : (
                                                        <XCircle className="text-gray-600" size={16} />
                                                    )}
                                                    <span className={`text-xs font-bold ${license.isActive ? 'text-green-700' : 'text-gray-700'}`}>
                                                        Status
                                                    </span>
                                                </div>
                                                <p className="text-lg font-bold text-gray-800">
                                                    {license.isActive ? 'Active' : 'Inactive'}
                                                </p>
                                            </div>
                                        </div>

                                        {expired && (
                                            <div className="mt-4 bg-red-100 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                                                <AlertCircle className="text-red-600" size={20} />
                                                <p className="text-sm font-bold text-red-700">
                                                    This license key has expired and is no longer valid.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Settings Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {cards.map((card, index) => {
                        const IconComponent = card.icon;
                        return (
                            <button
                                key={index}
                                onClick={() => setCurrentPage(card.page)}
                                className="group relative bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden"
                            >
                                <div className={`absolute inset-0 bg-gradient-to-br ${card.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                                <div className={`absolute -top-8 -right-8 w-32 h-32 rounded-full bg-gradient-to-br ${card.color} opacity-10 group-hover:opacity-20 transition-all duration-300 group-hover:scale-150`} />

                                <div className="relative z-10 flex flex-col items-center gap-5">
                                    <div className={`p-5 rounded-2xl bg-gradient-to-br ${card.color} shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                                        <IconComponent className="text-white" size={32} />
                                    </div>
                                    <div className="text-center">
                                        <h2 className="text-xl font-bold text-gray-800">{card.title}</h2>
                                        <p className="text-xs text-gray-500 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                            Click to manage
                                        </p>
                                    </div>
                                    <div className={`px-4 py-1.5 rounded-full ${card.bgColor} opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0`}>
                                        <span className={`text-xs font-bold ${card.textColor}`}>View Details →</span>
                                    </div>
                                </div>

                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                                </div>
                            </button>
                        );
                    })}
                </div>

                {licenses.length === 0 && (
                    <div className="bg-white rounded-3xl p-16 shadow-lg border border-gray-100 text-center">
                        <div className="inline-flex p-6 bg-gray-100 rounded-full mb-4">
                            <Key className="text-gray-400" size={48} />
                        </div>
                        <h3 className="text-2xl font-black text-gray-800 mb-2">No License Keys Found</h3>
                        <p className="text-gray-500 mb-6">Get started by adding your first license key</p>
                        {isSystemAdmin && (
                            <button
                                onClick={() => setShowAddForm(true)}
                                className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg transition-all hover:scale-105"
                            >
                                <Plus size={20} />
                                Add License Key
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}